import React from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ImageOff } from 'lucide-react';
import { ProductImage } from '../../types';
import { ImageUpload } from './ImageUpload';
import { SortableItem } from './SortableItem';

interface ImageGalleryProps {
  images: ProductImage[];
  onImagesChange: (images: ProductImage[]) => void;
  productId?: string; // Optional product ID for secure server upload
}

export function ImageGallery({ images, onImagesChange, productId }: ImageGalleryProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = images.findIndex(img => img.id === active.id);
      const newIndex = images.findIndex(img => img.id === over.id);

      const newImages = arrayMove(images, oldIndex, newIndex).map((img, idx) => ({
        ...img,
        order: idx
      }));

      console.log('🔄 Reordering images:', { oldIndex, newIndex, newOrder: newImages.map(i => i.id) });
      onImagesChange(newImages);
    }
  };

  const handleImagesAdded = (newImages: ProductImage[]) => {
    console.log('➕ Adding new images to gallery:', newImages);
    const updatedImages = [
      ...images,
      ...newImages.map((img, idx) => ({
        ...img,
        order: images.length + idx
      }))
    ];
    console.log('📋 Updated images list:', updatedImages);
    onImagesChange(updatedImages);
  };

  const handleRemoveImage = (imageId: string) => {
    console.log('🗑️ Removing image:', imageId);
    const updatedImages = images
      .filter(img => img.id !== imageId)
      .map((img, idx) => ({
        ...img,
        order: idx
      }));
    console.log('📋 Images after removal:', updatedImages);
    onImagesChange(updatedImages);
  };

  return (
    <div className="space-y-6">
      <ImageUpload 
        onImagesAdded={handleImagesAdded} 
        productId={productId} // Pass product ID for secure server upload
      />

      {images.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            Uploaded Images ({images.length})
          </h3>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map(img => img.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {images.map((image) => (
                  <SortableItem
                    key={image.id}
                    id={image.id}
                    image={image}
                    onRemove={handleRemoveImage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <ImageOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No images added yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Take photos or choose from your gallery
          </p>
        </div>
      )}
    </div>
  );
}